import { useContext, useState } from 'react'
import { v4 as uuid } from 'uuid'
import { db, timestamp } from '../../../firebase/config'
import { client } from '../../../hooks/Client'
import { Auth } from '../../../StateManagment/Auth'
import { SavedIcon } from '../../../StateManagment/SavedIcon'
import Modal from '../../common/Modal'

/**
 * Het venster om een projectnotitie toe te voegen of te bewerken. Gedeeld door
 * het tabblad 'Notities' op de projectpagina, het projectenoverzicht en de
 * dashboardkaart 'Projecten met ster', zodat er één manier van opslaan is.
 *
 * De notitie leeft in de EIGEN omgeving (`CompagnyID: client`) en wijst met
 * `ProjectID` naar het project; de klant ziet haar niet.
 * AI-CONTEXT: See docs/features/deccos-projectafspraken.md
 * Wordt alleen gerenderd zolang het open is: zo begint elke opening met de
 * velden van DEZE notitie (of leeg), zonder een tel de vorige te tonen, en
 * wist een binnenkomende snapshot niet wat de gebruiker typt.
 * AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
 *
 * @param {object} props
 * @param {boolean} props.modalOpen - Of het venster open is
 * @param {(open: boolean) => void} props.setModalOpen - Opent of sluit het venster
 * @param {string} props.projectId - CompagnyID van het project
 * @param {string} [props.projectName] - Naam van het project; komt in de kop als je buiten de projectpagina werkt
 * @param {{docid: string, Title?: string, Body?: string}} [props.note] - De notitie om te bewerken; ontbreekt = nieuwe notitie
 * @param {number} props.position - Volgnummer voor een nieuwe notitie (aantal notities van het project + 1)
 * @returns {JSX.Element}
 */
const ProjectNoteModal = ({ modalOpen, setModalOpen, projectId, projectName, note, position }) => {
    const [auth] = useContext(Auth)
    const [, setSaved] = useContext(SavedIcon)

    // Eenmalig gevuld bij het aanhaken (= openen), niet bij elke snapshot.
    const [titleInput, setTitleInput] = useState(note?.Title || '')
    const [bodyInput, setBodyInput] = useState(note?.Body || '')
    const [formError, setFormError] = useState('')
    const [saving, setSaving] = useState(false)
    const noteDocid = note?.docid

    const saveNote = async () => {
        if (saving) return
        const title = titleInput.trim()
        if (!title) {
            setFormError('Geef de notitie een titel.')
            return
        }

        setSaving(true)
        try {
            if (noteDocid) {
                await db.collection('ProjectNotes').doc(noteDocid).update({
                    Title: title,
                    Body: bodyInput,
                })
            } else {
                await db.collection('ProjectNotes').add({
                    ID: uuid(),
                    ProjectID: projectId,
                    CompagnyID: client,
                    Compagny: client,
                    Title: title,
                    Body: bodyInput,
                    Position: position,
                    User: auth?.ID || '',
                    Timestamp: timestamp,
                })
            }
            // Eerst de eigen state, dan sluiten: na het sluiten is dit venster weg
            // en hoort het niets meer aan zichzelf te zetten.
            setSaving(false)
            setSaved('flex')
            setModalOpen(false)
        } catch (err) {
            // Het venster blijft open, met de tekst er nog in: niets is kwijt.
            // eslint-disable-next-line no-console
            console.error('Failed to save project note:', err)
            setSaving(false)
            setFormError(`Opslaan is mislukt: ${err.message}`)
        }
    }

    const heading = noteDocid ? 'Notitie bewerken' : 'Notitie toevoegen'

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            pageHeader={projectName ? `${heading} — ${projectName}` : heading}
            confirm={saveNote}
            confirmDisabled={saving}
            confirmLabel={'Opslaan'}
            content={
                <div className="project-afspraken-form">
                    <label className="project-afspraken-field">
                        <span>Titel</span>
                        <input
                            id="project-note-title"
                            type="text"
                            value={titleInput}
                            onChange={(e) => setTitleInput(e.target.value)}
                        />
                    </label>
                    <label className="project-afspraken-field">
                        <span>Notitie</span>
                        <textarea
                            id="project-note-body"
                            rows={6}
                            value={bodyInput}
                            onChange={(e) => setBodyInput(e.target.value)}
                        />
                    </label>
                    {formError && <p className="project-afspraken-error" role="alert">{formError}</p>}
                </div>
            }
        />
    )
}

export default ProjectNoteModal
